import { useState } from "react";
import Seo from "@/components/Seo";
import { Reveal, GoldLine } from "@/components/Reveal";
import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { postEnquiry, openBooking } from "@/lib/api";
import { ADDRESS, PHONE_DISPLAY, PHONE_TEL, EMAIL, INSTAGRAM_URL, FACEBOOK_URL, waLink, WA_MESSAGES, BRAND_FULL } from "@/config/site";

const inputCls = "w-full bg-charcoal/60 border border-gold/20 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-sand/50 transition-colors";

const Contact = () => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error("Please share your name and message.");
            return;
        }
        setBusy(true);
        try {
            await postEnquiry(form);
            toast.success("Message received. We will be in touch shortly.");
            setForm({ name: "", phone: "", email: "", message: "" });
        } catch {
            toast.error("Something went wrong — please try WhatsApp.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="pt-32 sm:pt-40 pb-24 min-h-screen">
            <Seo
                title="Contact BSK Jewellers — Rani Ka Bagh, Amritsar"
                siteName="BSK Jewellers"
                description={`Contact BSK Jewellers (${BRAND_FULL}), ${ADDRESS}. Call or WhatsApp ${PHONE_DISPLAY}, send a message, or book a private appointment.`}
            />
            <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
                <Reveal>
                    <span className="eyebrow">Contact</span>
                    <h1 className="font-serif text-4xl sm:text-6xl leading-[1.05] mt-5">
                        We Would Love to <span className="italic gold-text">Hear From You</span>
                    </h1>
                    <GoldLine delay={0.15} className="w-20 my-7" />
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mt-6">
                    <Reveal>
                        <div className="space-y-1">
                            <p className="font-serif text-2xl">BSK JEWELLERS</p>
                            <p className="text-[10px] tracking-[0.3em] uppercase text-sand">{BRAND_FULL}</p>
                            <p className="text-sm text-sand mt-4 flex items-start gap-3"><MapPin size={15} className="text-gold mt-1 shrink-0" />{ADDRESS}</p>
                        </div>
                        <div className="mt-8 space-y-4">
                            <a href={`tel:${PHONE_TEL}`} data-testid="contact-call-btn" className="flex items-center gap-4 border border-gold/20 hover:border-gold/60 p-5 transition-colors group">
                                <Phone size={18} className="text-gold" strokeWidth={1.5} />
                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase text-sand">Call Us</p>
                                    <p className="text-sm text-ivory group-hover:text-gold transition-colors">{PHONE_DISPLAY}</p>
                                </div>
                            </a>
                            <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="flex items-center gap-4 border border-gold/20 hover:border-gold/60 p-5 transition-colors group">
                                <MessageCircle size={18} className="text-gold" strokeWidth={1.5} />
                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase text-sand">WhatsApp</p>
                                    <p className="text-sm text-ivory group-hover:text-gold transition-colors">{PHONE_DISPLAY}</p>
                                </div>
                            </a>
                            <div className="flex items-center gap-4 border border-gold/20 p-5">
                                <Mail size={18} className="text-gold" strokeWidth={1.5} />
                                <div>
                                    <p className="text-[10px] tracking-[0.25em] uppercase text-sand">Email</p>
                                    <p className="text-sm text-ivory">{EMAIL}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-4 mt-8">
                            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="text-[11px] tracking-[0.22em] uppercase text-sand hover:text-gold transition-colors border-b border-gold/30 pb-0.5">Instagram</a>
                            <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" data-testid="contact-facebook" className="text-[11px] tracking-[0.22em] uppercase text-sand hover:text-gold transition-colors border-b border-gold/30 pb-0.5">Facebook</a>
                            <button onClick={() => openBooking()} data-testid="contact-book-btn" className="text-[11px] tracking-[0.22em] uppercase text-sand hover:text-gold transition-colors border-b border-gold/30 pb-0.5">Book Appointment</button>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <form onSubmit={submit} className="border border-gold/20 bg-charcoal/30 p-7 sm:p-10 space-y-5" data-testid="contact-form">
                            <p className="eyebrow !text-[10px]">Send a Message</p>
                            <input required value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} placeholder="Your name *" />
                            <input value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className={inputCls} placeholder="Phone" />
                            <input type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} placeholder="Email" />
                            <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" className={inputCls} placeholder="How can we help? *" />
                            <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="w-full bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-gold-bright transition-colors disabled:opacity-60">
                                {busy ? "Sending…" : "Send Message"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </main>
    );
};

export default Contact;
